/**
 * Umr Calendar — life grid. Each cell = 1 year.
 * Colors from user's annotations (stage per year).
 * Event dots for years with custom events.
 * Tap → year detail sheet with stage picker + event editor + stats.
 */

import { memo, useMemo, useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { calcAge } from '@turnifly/shared/utils'
import {
  useLifeAnnotationsStore,
  getStageColorForYear,
  getPresetById,
  type YearAnnotation,
} from '@turnifly/shared/stores'
import { YearDetailSheet } from './year-detail-sheet'
import type { FamousMilestone, TimelineEvent } from '@turnifly/shared/api'

interface UmrCalendarProps {
  birthDate: string
  lifeExpectancy: number
  famousMilestones: FamousMilestone[]
  userAchievements: TimelineEvent[]
}

interface CellData {
  yearOfLife: number
  calendarYear: number
  isLived: boolean
  isCurrent: boolean
  color: string       // stage color or default
  hasEvents: boolean   // custom user events
  hasAutoEvents: boolean // turnifly achievements
}

const COLS = 10
const DEFAULT_LIVED_COLOR = '#3B82F6'  // blue when no stage set
const DEFAULT_FUTURE_COLOR = 'rgba(255,255,255,0.03)'

export const UmrCalendar = memo(function UmrCalendar({
  birthDate,
  lifeExpectancy,
  famousMilestones,
  userAchievements,
}: UmrCalendarProps) {
  const { t } = useTranslation()
  const currentAge = calcAge(birthDate)
  const birthYear = new Date(birthDate).getFullYear()
  const { annotations } = useLifeAnnotationsStore()
  const [selectedYear, setSelectedYear] = useState<number | null>(null)

  // Achievement years
  const autoEventYears = useMemo(() => {
    const s = new Set<number>()
    userAchievements.forEach((a) => s.add(new Date(a.date).getFullYear()))
    return s
  }, [userAchievements])

  const cells = useMemo<CellData[]>(() => {
    const arr: CellData[] = []
    for (let i = 0; i < lifeExpectancy; i++) {
      const yearOfLife = i + 1
      const calendarYear = birthYear + yearOfLife
      const isLived = yearOfLife <= currentAge
      const isCurrent = yearOfLife === currentAge
      const stageColor = getStageColorForYear(calendarYear, annotations)
      const color = stageColor ?? (isLived ? DEFAULT_LIVED_COLOR : DEFAULT_FUTURE_COLOR)
      const ann = annotations[calendarYear] as YearAnnotation | undefined
      const hasEvents = (ann?.events?.length ?? 0) > 0
      const hasAutoEvents = autoEventYears.has(calendarYear)
      arr.push({ yearOfLife, calendarYear, isLived, isCurrent, color, hasEvents, hasAutoEvents })
    }
    return arr
  }, [birthYear, currentAge, lifeExpectancy, annotations, autoEventYears])

  const handleTap = useCallback((cell: CellData) => {
    setSelectedYear(cell.calendarYear)
  }, [])

  const selectedCell = selectedYear !== null
    ? cells.find((c) => c.calendarYear === selectedYear)
    : null

  // Unique stages in use for legend
  const usedStageIds = useMemo(() => {
    const ids = new Set<string>()
    Object.values(annotations).forEach((ann) => {
      if (ann.stageId) ids.add(ann.stageId)
    })
    return ids
  }, [annotations])

  return (
    <>
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.03] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <div>
            <h3 className="text-sm font-display font-bold text-text">{t('life.umrCalendar')}</h3>
            <p className="text-[10px] text-hint mt-0.5">{t('life.eachCellYear')}</p>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/[0.05] border border-white/[0.06]">
            <span className="text-xs font-bold text-text tabular-nums">{currentAge}</span>
            <span className="text-[10px] text-hint">/ {lifeExpectancy}</span>
          </div>
        </div>

        {/* Grid */}
        <div className="px-3 pb-3">
          <div className="grid gap-[3px]" style={{ gridTemplateColumns: `repeat(${COLS}, 1fr)` }}>
            {cells.map((cell) => (
              <Cell key={cell.yearOfLife} cell={cell} onTap={handleTap} />
            ))}
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 pb-3 border-t border-white/[0.04] pt-2">
          {usedStageIds.size > 0 ? (
            Array.from(usedStageIds).map((id) => {
              const p = getPresetById(id)
              if (!p) return null
              return (
                <div key={id} className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-[3px]" style={{ backgroundColor: p.color }} />
                  <span className="text-[7px]">{p.icon}</span>
                  <span className="text-[7px] text-hint">{t(`life.stages.${p.id}`)}</span>
                </div>
              )
            })
          ) : (
            <>
              <LegendDot color={DEFAULT_LIVED_COLOR} label={t('life.lived')} />
              <LegendDot color="rgba(255,255,255,0.12)" label={t('life.future')} />
            </>
          )}
          <LegendDot color="#52DAFF" label={t('life.current')} ring />
          <LegendDot color="#F59E0B" label={t('life.achievement')} />
        </div>
      </div>

      {/* Year detail sheet */}
      {selectedCell && (
        <YearDetailSheet
          isOpen
          onClose={() => setSelectedYear(null)}
          yearOfLife={selectedCell.yearOfLife}
          calendarYear={selectedCell.calendarYear}
          isLived={selectedCell.isLived}
          isCurrent={selectedCell.isCurrent}
          famousMilestones={famousMilestones}
          userAchievements={userAchievements}
        />
      )}
    </>
  )
})

// ── Cell ──

const Cell = memo(function Cell({ cell, onTap }: { cell: CellData; onTap: (c: CellData) => void }) {
  const { yearOfLife, isCurrent, isLived, color, hasEvents, hasAutoEvents } = cell

  let bg: string
  let textColor: string

  if (isCurrent) {
    bg = color
    textColor = '#FFFFFF'
  } else if (isLived) {
    // Solid stage color, visible
    bg = color + 'AA'  // ~67% opacity
    textColor = 'rgba(255,255,255,0.8)'
  } else {
    bg = DEFAULT_FUTURE_COLOR
    textColor = 'rgba(255,255,255,0.12)'
  }

  return (
    <motion.button
      whileTap={{ scale: 0.88 }}
      onClick={() => onTap(cell)}
      className={`relative flex items-center justify-center aspect-square rounded-md tap-highlight-transparent ${
        isCurrent ? 'ring-2 ring-accent/50' : isLived ? '' : 'border border-white/[0.04]'
      }`}
      style={{ backgroundColor: bg }}
    >
      <span className="text-[9px] font-semibold tabular-nums select-none" style={{ color: textColor }}>
        {yearOfLife}
      </span>

      {/* Current year pulse */}
      {isCurrent && (
        <motion.div
          className="absolute inset-0 rounded-md border-[1.5px] border-accent/40"
          animate={{ opacity: [0.3, 0.8, 0.3] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      )}

      {/* Event indicators */}
      {(hasEvents || hasAutoEvents) && (
        <div className="absolute -top-[2px] -right-[2px] flex gap-[1px]">
          {hasEvents && (
            <div className="w-[5px] h-[5px] rounded-full bg-amber-400 shadow-[0_0_3px_rgba(245,158,11,0.6)]" />
          )}
          {hasAutoEvents && !hasEvents && (
            <div className="w-[4px] h-[4px] rounded-full bg-primary/60" />
          )}
        </div>
      )}
    </motion.button>
  )
})

// ── Legend ──

function LegendDot({ color, label, ring }: { color: string; label: string; ring?: boolean }) {
  return (
    <div className="flex items-center gap-1">
      {ring ? (
        <span className="w-2.5 h-2.5 rounded-[3px] ring-[1.5px] ring-accent/50 bg-accent/20" />
      ) : (
        <span className="w-2.5 h-2.5 rounded-[3px]" style={{ backgroundColor: color }} />
      )}
      <span className="text-[7px] text-hint">{label}</span>
    </div>
  )
}
